import {Component, OnDestroy, OnInit, inject, output, input, effect} from '@angular/core';
import {Subscription} from "rxjs";
import {
    AbstractControl,
    FormBuilder,
    UntypedFormGroup,
    Validators,
    FormsModule,
    ReactiveFormsModule
} from "@angular/forms";
import {DataService} from "../../../../services/data.service";
import {AccessService} from "../../../../services/access.service";
import {FormService} from "../../../../services/form.service";
import {ToastrService} from "ngx-toastr";
import {Right, TeamRole, ViewMode} from "../../../interfaces";
import {NgIf, NgFor, DatePipe} from '@angular/common';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {DisableControlDirective} from '../../../../directives/disable-control.directive';
import {TeamRolePipe} from '../../../../pipes/team-role.pipe';

@Component({
    selector: 'app-project-approvals',
    templateUrl: './project-approvals.component.html',
    styleUrl: './project-approvals.component.scss',
    imports: [NgIf, TooltipModule, FormsModule, ReactiveFormsModule, NgFor, DisableControlDirective, DatePipe, TeamRolePipe]
})
export class ProjectApprovalsComponent implements OnInit, OnDestroy {

    accessService = inject(AccessService);
    formService = inject(FormService);
    private dataService = inject(DataService);
    private fb = inject(FormBuilder);
    private toastrService = inject(ToastrService);

    readonly PID = input.required<any>();
    readonly reloadTrigger = input<number>(0);

    readonly changeProject = output();
    readonly changeViewMode = output<boolean>();

    readonly ViewMode = ViewMode;

    viewMode!: ViewMode;
    project: any;
    files: any[] = [];
    links: any[] = [];
    form!: UntypedFormGroup;

    oldApprovals = {
        project: {
            show: false,
            entries: [],
        },
        completion: {
            show: false,
            entries: [],
        }
    }

    private approvalsChangesList$: Subscription[] = [];

    private reloadEffect = effect(() => {
        if (this.reloadTrigger() > 0) {
            this.getProject();
        }
    })

    ngOnInit(): void {
        this.viewMode = ViewMode.VIEW;

        this.form = this.fb.group({
            id: [null],
            approvalsProject: this.fb.array([]),
            approvalsCompletion: this.fb.array([]),
        });

        if (this.PID() !== '0') {
            this.getProject();
        }
    }

    ngOnDestroy(): void {
        this.approvalsChangesList$.forEach(item => item.unsubscribe());
    }

    getProject(): void {
        this.dataService.request('Project/get', {
            PID: this.PID()
        }).subscribe(response => {
            this.project = response.project;

            this.project.approvals.forEach(item => {
                if (item.old == '1') {
                    this.oldApprovals[item.type].entries.push(item);
                }
            });

            const roleMapping = {
                orderer: 2,
                lead: 1,
                customer: 0,
            }

            this.oldApprovals.project.entries.sort((a, b) => {
                if (roleMapping[a.role] < roleMapping[b.role]) return 1;
                else if (roleMapping[a.role] > roleMapping[b.role]) return -1;
                else return 0;
            })

            this.oldApprovals.completion.entries.sort((a, b) => {
                if (roleMapping[a.role] < roleMapping[b.role]) return 1;
                else if (roleMapping[a.role] > roleMapping[b.role]) return -1;
                else return 0;
            })

            this.patchForm();
        });
    }

    patchForm(): void {
        this.form.patchValue({
            id: this.project.id,
        });

        this.formService.getFormArray(this.form, 'approvalsProject').clear();
        this.formService.getFormArray(this.form, 'approvalsCompletion').clear();
        this.project.approvals.forEach(item => {
            if (item.type == 'project' && item.old == '0') {
                this.addFormItemApproval(item, 'approvalsProject');
            }
            if (item.type == 'completion' && item.old == '0') {
                this.addFormItemApproval(item, 'approvalsCompletion');
            }
        });
    }

    addFormItemApproval(data: any = {}, control): void {
        const newGroup = this.fb.group({
            id: [data.id || null],
            id_project: [data.id_project || this.project.id],
            approver: [data.approver || null],
            approved: [data.approved || null],
            entered: [data.entered || null],
            created: [data.created || null],
            updated: [data.updated || null],
            note: [data.note || null],
            role: [data.role || null],
        });

        this.approvalsChangesList$.push(newGroup.get('approved').valueChanges.subscribe(val => {
            if (val != null) {
                if (val == '1') {
                    newGroup.get('note').removeValidators(Validators.required);
                } else {
                    newGroup.get('note').addValidators(Validators.required);
                }
                newGroup.get('note').updateValueAndValidity();
            }
        }));

        this.formService.getFormArray(this.form, control).push(newGroup);

        // sort team members by their role
        this.formService.getFormArray(this.form, control).patchValue(
            this.formService.getFormArray(this.form, control).value.sort((a, b) => {

                const roleMapping = {
                    orderer: 2,
                    lead: 1,
                    customer: 0,
                }

                if (roleMapping[a.role] < roleMapping[b.role]) {
                    return 1;
                } else if (roleMapping[a.role] > roleMapping[b.role]) {
                    return -1;
                } else {
                    return 0;
                }

                // return roleMapping[a.role] <=> roleMapping[b.role];
            })
        );
    }

    setMode(viewMode: ViewMode): void {
        this.viewMode = viewMode;
        if (this.viewMode == ViewMode.EDIT) {
            this.changeViewMode.emit(true);
        } else {
            this.changeViewMode.emit(false);
        }
    }

    update(): void {
        this.dataService.request('Project/updateApprovals', {
            project: this.form.getRawValue(),
        }).subscribe(() => {
            this.toastrService.success('Erfolgreich gespeichert')
            this.setMode(ViewMode.VIEW);
            this.getProject();
            this.changeProject.emit();
        });
    }

    cancelEdit(): void {
        this.patchForm();
        this.setMode(ViewMode.VIEW);
    }

    // check if the current user can edit the approval
    canEditApproval(entry: AbstractControl<any>): boolean {

        if (this.accessService.hasRights(Right.PMO)) {
            return true;
        }

        if (entry.get('role').value == TeamRole.Lead && this.accessService.hasRoles(this.project, TeamRole.Lead)) {
            return true;
        }

        if (entry.get('role').value == TeamRole.Customer && this.accessService.hasRoles(this.project, TeamRole.Customer)) {
            return true;
        }

        if (entry.get('role').value == TeamRole.Orderer && this.accessService.hasRoles(this.project, TeamRole.Orderer)) {
            return true;
        }

        return false;
    }

    toggleOldApprovals(type: string) {
        this.oldApprovals[type].show = !this.oldApprovals[type].show;
    }
}
